import React from 'react';
import useModal from '../../../hooks/useModal';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../../components/AutoComplete/AutoCompleteRep';
import AutoCompleteSymbol from '../../../components/AutoComplete/AutoCompleteSymbol';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import {
  SelectSide,
  SelectStatus,
} from '../../../components/MultipleSelect/SelectSystemCode';
import { SelectEntryType } from '../../../components/MultipleSelect/SelectEntryType';
import Text from '../../../components/Typography/Text';
import {
  notifyInfo,
  notifyError,
  notifyWarning,
} from '../../../components/Messages/Notification';
import { getActivitySummary, listActivities } from '../../../services/ActivityService';
import { getPreviousDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderAlign,
  CustomBodyRenderQty,
  CustomBodyRenderBoolean,
} from '../../../components/Table/CustomBodyRender';
import { formatPbDate, formatPbDateTimeWithMillis } from '../../../lib/fmt';
import Checkbox from '../../../components/Misc/Checkbox';
import { formatQty, formatCurrency } from '../../../lib/fmt';
import camelCase from 'lodash/camelCase';
import ActivityOtherFeesTable from './Components/ActivityOtherFeesTable';
import { Tooltip } from '@material-ui/core';
import TextField from '../../../components/Textfields/TextField';
import { DynamicFeed as ViewMoreIcon, Receipt } from '@material-ui/icons';
import ActivityDetailsModal from './Components/ActivityDetailsModal';
import {
  protoTimeSpanObjectToString,
  protoDatTimeObjectToDateWithMilliseconds,
} from '../../../services/ConvertService';
import ActivityReportDetails from './Components/ActivityReportDetails';
import { identity } from 'lodash';
import IconButton from '../../../components/Buttons/IconButton';

export default function Activity({ params, page }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      description: '',
      toDate: '',
      branch: '',
      accountNo: '',
      masterAccountNo: '',
      rep: '',
      accountName: '',
      symbol: '',
      compress: false,
      correspondent: '',
      dateType: 'Trade Date',
      searchType: 'Uncompress',
      searchTypeBool: false,
      breakdownOtherFees: false,
      side: [],
      sideString: '',
      status: [],
      statusString: '',
      entryType: [],
      entryTypeString: '',
    })
  );
  const [footerData, setFooterData] = React.useState({});
  const [pagination, setPagination] = React.useState({});
  const [otherFeeColumns, setOtherFeeColumns] = React.useState([]);
  const [rowData, setRowData] = React.useState({});
  const [open, setOpen] = React.useState(false);

  const init = async () => {
    const {
      fromDate,
      toDate,
      branch,
      accountNo,
      masterAccountNo,
      rep,
      accountName,
      symbol,
      description,
    } = searchData;

    const previousDate = await getPreviousDate();

    if (searchData.statusString !== '' && searchData.statusString) {
      const tempStr = searchData.statusString.split(',');
      tempStr.forEach((str) => {
        let tempObj = {
          code: str,
          description: str,
          note: '',
          subType: 'Transaction',
        };
        searchData.status.push(tempObj);
      });
    }

    if (searchData.sideString !== '' && searchData.sideString) {
      const tempStr = searchData.sideString.split(',');
      tempStr.forEach((str) => {
        let tempObj = {
          code: str,
          description: str,
          note: '',
          subType: 'Option',
        };
        searchData.side.push(tempObj);
      });
    }

    if (searchData.entryTypeString !== '' && searchData.entryTypeString) {
      const tempStr = searchData.entryTypeString.split(',');
      tempStr.forEach((str) => {
        str = str.replace(' - ', '-');
        const str2 = str.split('-');
        let tempObj = {
          entryType: str2[0],
          entryTypeDescription: str2[1],
        };
        searchData.entryType.push(tempObj);
      });
    }

    setSearchData({
      ...searchData,
      fromDate: fromDate ? fromDate : previousDate,
      toDate: toDate ? toDate : previousDate,
      branch: branch ? branch : '',
      accountNo: accountNo ? accountNo : '',
      masterAccountNo: masterAccountNo ? masterAccountNo : '',
      rep: rep ? rep : '',
      accountName: accountName ? accountName : '',
      symbol: symbol ? symbol : '',
      description: description ? description : '',
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const dynamicColumns = otherFeeColumns.map((columnName) => {
    return {
      name: camelCase(columnName),
      label: columnName,
      type: columnType.amount,
      addFooter: true,
      options: {
        filter: false,
      },
    };
  });

  const toggleState = () => {
    if (searchData.searchType == 'Compress') {
      return false;
    } else {
      return true;
    }
  };

  const columns = [
    {
      name: '',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              {tempFilteredRows[dataIndex]?.compressCount > 1 && (
                <Tooltip title="View More" arrow>
                  <div>
                    <IconButton
                      icon="viewMore"
                      aria-label="back"
                      // disabled={loading.cancel}
                      onClick={() => {
                        handleOpen(tempFilteredRows[dataIndex]);
                      }}
                    ></IconButton>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'trnsId',
      label: 'TRNS ID',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'reportActivityId',
      label: 'Activity ID',
      options: {
        display: false,
      },
    },
    {
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'tradeDate',
      label: 'Trade Date',
      type: columnType.date,
    },
    {
      // Already converted to string in handlesearch
      name: 'settleDate',
      label: 'Settle Date',
      type: columnType.date,
    },
    {
      name: 'entryType',
      label: 'Entry Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'marginType',
      label: 'Margin Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'side',
      label: 'Side',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'qty',
      label: 'Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'grossAmt',
      label: 'Gross Amount',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'regFee',
      label: 'Reg Fee',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'commission',
      label: 'Commission',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tafFee',
      label: 'Taf Fee',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'netAmt',
      label: 'Net Amount',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        sort: toggleState(),
        filter: toggleState(),
      },
    },
    {
      name: 'otherFees',
      label: 'Fee Details',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        sort: toggleState(),
        filter: toggleState(),
      },
    },
    {
      name: 'otherFees2',
      label: 'Other Fees',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
      type: columnType.amount,
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        sort: toggleState(),
        filter: toggleState(),
      },
    },
    {
      name: 'compressCount',
      label: 'Compress Count',
      options: {
        display: false,
      },
    },
    {
      name: 'tradeAt',
      label: 'Trade At',
      options: {
        display: true,
        customBodyRenderLite: (dataIndex) => {
          return protoDatTimeObjectToDateWithMilliseconds(
            tempFilteredRows[dataIndex]?.tradeAt
          );
        },
        sort: false,
        filter: false,
      },
    },
    {
      name: 'dvpConfirmNo',
      label: 'DVP Confirm No',
      options: {
        display: false,
      },
    },
    {
      name: 'contraSubAccountNo',
      label: 'Contra Sub Account No',
      options: {
        display: false,
      },
    },
    {
      name: 'isArchived',
      label: 'Is Archived',
      options: {
        customBodyRender: CustomBodyRenderBoolean,
        filter: false,
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };

  const handleSearchDataChange = (e, checkboxValue) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    if (input.name === 'searchTypeBool' && checkboxValue === true) {
      searchData.statusString = '';
      searchData.status = [];
    }

    setSearchData({
      ...searchData,
      [input.name]:
        checkboxValue === true || checkboxValue === false ? checkboxValue : input.value,
    });
  };

  const handleSearch = async (paginationChange) => {
    let valid = true;
    if (!searchData.fromDate) {
      notifyWarning('From Date is required.');
      valid = false;
    }
    if (!searchData.toDate) {
      notifyWarning('To Date is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }

    if (searchData.searchTypeBool === true) searchData.searchType = 'Compress';
    else searchData.searchType = 'Uncompress';

    let start = true;

    if (searchData.status?.length !== 0 && searchData.status) {
      searchData.status.forEach((str) => {
        if (start) {
          searchData.statusString = str.code;
          start = false;
        } else {
          searchData.statusString += ',' + str.code;
        }
      });
    } else {
      searchData.statusString = '';
    }

    start = true;

    if (searchData.side?.length !== 0 && searchData.side) {
      searchData.side.forEach((str) => {
        if (start) {
          searchData.sideString = str.code;
          start = false;
        } else {
          searchData.sideString += ',' + str.code;
        }
      });
    } else {
      searchData.sideString = '';
    }

    start = true;

    if (searchData.entryType?.length !== 0 && searchData.entryType) {
      searchData.entryType.forEach((str) => {
        if (start) {
          searchData.entryTypeString = str.entryType + ' - ' + str.entryTypeDescription;
          start = false;
        } else {
          searchData.entryTypeString +=
            ',' + str.entryType + ' - ' + str.entryTypeDescription;
        }
      });
    } else {
      searchData.entryTypeString = '';
    }

    try {
      const { status, side, entryType, ...rest } = searchData;

      QueryParam.set(rest);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      // bug fix when sorted breakdown fees then unchecked breakdown fees, missing field
      if (
        !searchData.breakdownOtherFees &&
        paginationCopy.sortName &&
        otherFeeColumns.length
      ) {
        for (let index = 0; index < otherFeeColumns.length; index++) {
          const colName = camelCase(otherFeeColumns[index]);
          if (paginationCopy.sortName === colName) {
            paginationCopy.sortName = '';
            paginationCopy.sortDirection = '';

            break;
          }
        }
      }

      await getGridRows(searchData, paginationCopy);
      if (paginationChange !== true) {
        await getGridSummary(searchData, paginationCopy);
      }
    } catch (error) {
      setLoading(false);
      notifyError(error.message);
      console.log(error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const getGridRows = async (searchData, paginationCopy) => {
    setLoading({ ...loading, search: true });
    const data = await listActivities(searchData, paginationCopy);
    setOtherFeeColumns(data.otherFeeTypesList);
    setRows(manipulateDynamicData(data));
  };

  const getGridSummary = async (searchData, paginationCopy) => {
    const data = await getActivitySummary(searchData, paginationCopy);
    if (page !== 'Physical Securities') {
      notifyInfo(data.totalRows + ' search results.');
    }
    paginationCopy.count = data.totalRows;
    setPagination(paginationCopy);
    const fdata = {
      qty: formatQty(data.qty),
      grossAmt: formatCurrency(data.grossAmt),
      regFee: formatCurrency(data.regFee),
      commission: formatCurrency(data.commission),
      tafFee: formatCurrency(data.tafFee),
      fees: formatCurrency(data.fees),
      netAmt: formatCurrency(data.netAmt),
      otherFees2: formatCurrency(data.otherFees2),
      taxWithholding: formatCurrency(data.taxWithholding),
      executionQty: formatQty(data.executionQty),
      executionNetAmt: formatCurrency(data.executionNetAmt),
      currencyAmt: data.currencyAmt,
    };
    if (searchData.breakdownOtherFees) {
      for (let index = 0; index < data.otherFeeTypesList.length; index++) {
        const colName = camelCase(data.otherFeeTypesList[index]);
        const colValue = data.otherFeeValuesList[index];
        fdata[colName] = formatCurrency(colValue);
      }
    }

    setFooterData(fdata);
  };

  const manipulateDynamicData = (data) => {
    if (searchData.breakdownOtherFees) {
      return data.activitiesList.map((i) => {
        for (let index = 0; index < data.otherFeeTypesList.length; index++) {
          const colName = camelCase(data.otherFeeTypesList[index]);
          i[colName] = i.otherFeeValuesList[index];
        }

        delete i.otherFeeValuesList;
        return i;
      });
    }

    return data.activitiesList;
  };

  const getCsvData = async () => {
    if (rows.length === 0) {
      return [];
    }
    const data = await listActivities(searchData);
    const activitiesList = manipulateDynamicData(data);

    const rowsCopyOutput = activitiesList.map((data) => {
      let tempDate1 = formatPbDate(data.effSettleDate);
      data.effSettleDate = tempDate1;

      let tempDate2 = formatPbDate(data.effTradeDate);
      data.effTradeDate = tempDate2;

      let tempDate3 = formatPbDate(data.effAtlSettleDate);
      data.effAtlSettleDate = tempDate3;

      let tempDate4 = formatPbDate(data.effAtlTradeDate);
      data.effAtlTradeDate = tempDate4;

      let tradeAt = formatPbDateTimeWithMillis(data?.tradeAt);

      data.tradeAt = tradeAt;

      delete data.createdAt;

      return data;
    });

    return rowsCopyOutput;
  };

  const handleOpen = (selectedRow) => {
    let data = { ...selectedRow };
    data.createdAt = data.createdAt
      ? protoTimeSpanObjectToString(data.createdAt, 'MM/DD/YYYY hh:mm:ss a')
      : '--';
    data.tradeAt = data.tradeAt
      ? protoTimeSpanObjectToString(data.tradeAt, 'hh:mm:ss a')
      : '--';
    setRowData(data);
    setOpen(true);
  };

  const handleClose = async () => {
    setOpen(false);
  };

  const handleCloseReportDetails = () => {
    setModalProps({
      open: false,
      value: {},
    });
  };

  const [modal, setModalProps] = useModal(ActivityReportDetails, {
    open: false,
    value: {},
    onClose: () => handleCloseReportDetails,
  });

  const renderDetailsButton = (reportId, currency, isArchived) => {
    if (reportId) {
      return [
        <IconButton
          key={1}
          icon="receipt"
          text="View Details"
          color="inherit"
          onClick={() => {
            setModalProps({
              open: true,
              value: {
                id: reportId,
                currency: currency,
                archived: isArchived,
              },
            });
          }}
        />,
      ];
    }
    return [];
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Activity" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectSystemCode
              name="dateType"
              label="Date Type"
              type="Date Type"
              subType="Activity Report"
              value={searchData.dateType}
              onChange={handleSearchDataChange}
              disableBlank
            ></SelectSystemCode>
          </div>
          <div className="grd-cell">
            <DateTimePicker
              type="date"
              name="fromDate"
              label="From Date"
              value={searchData.fromDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell">
            <DateTimePicker
              type="date"
              name="toDate"
              label="To Date"
              value={searchData.toDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              freeSolo
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteBranch
              name="branch"
              value={searchData.branch}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              freeSolo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              freeSolo
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              name="rep"
              value={searchData.rep}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectEntryType
              name="entryType"
              label="Entry Type"
              value={searchData.entryType}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              value={searchData.symbol}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSide
              name="side"
              label="Side"
              type="Side"
              subType="Equity"
              value={searchData.side}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <TextField
              name="description"
              label="Description"
              placeholder="Description"
              type="text"
              value={searchData.description}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectStatus
              name="status"
              label="Status"
              type="Status"
              subType="Transaction"
              value={searchData.status}
              onChange={handleSearchDataChange}
              disabled={searchData.searchTypeBool}
            />
          </div>
          <div className="grd-cell">
            <Checkbox
              mt={40}
              name="searchTypeBool"
              label="Compress"
              checked={searchData.searchTypeBool}
              onChange={handleSearchDataChange}
            />
          </div>

          <div className="grd-cell">
            <Checkbox
              mt={40}
              name="breakdownOtherFees"
              label="Breakdown Other Fees"
              checked={searchData.breakdownOtherFees}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell"></div>
        </div>

        {searchData.breakdownOtherFees && otherFeeColumns.length ? (
          <div className="mt-20">
            <ActivityOtherFeesTable
              title={'Activity - Other Fees Breakdown'}
              data={rows}
              columns={[...columns, ...dynamicColumns]}
              options={options}
              footerData={footerData}
              onSearch={handleSearch}
              pagination={pagination}
              setPagination={setPagination}
              getCsvData={getCsvData}
              renderRowDetailsAddOnButtons={(row) => {
                const reportId =
                  row.find((item) => item.label === 'Activity ID')?.value || 0;
                const currency =
                  row.find((item) => item.label === 'Currency')?.value || '';
                const archived =
                  row.find((item) => item.label === 'Is Archived')?.value || 0;
                return renderDetailsButton(reportId, currency, archived);
              }}
              // additionalCell={
              //   userAccess === 'Full' || userAccess === 'Full Without Import'
              //     ? true
              //     : false
              // }
              // modalTable={params.modalTable}
            />
          </div>
        ) : (
          <div className="mt-20">
            <Table
              title={'Activity'}
              data={rows}
              columns={columns}
              options={options}
              footerData={footerData}
              onSearch={handleSearch}
              pagination={pagination}
              setPagination={setPagination}
              getCsvData={getCsvData}
              renderRowDetailsAddOnButtons={(row) => {
                const reportId =
                  row.find((item) => item.label === 'Activity ID')?.value || 0;
                const currency =
                  row.find((item) => item.label === 'Currency')?.value || '';
                const archived =
                  row.find((item) => item.label === 'Is Archived')?.value || 0;
                return renderDetailsButton(reportId, currency, archived);
              }}
            />
          </div>
        )}
      </div>
      {open && (
        <ActivityDetailsModal
          onClose={handleClose}
          open={open}
          value={rowData}
          breakdownOtherFees={searchData.breakdownOtherFees}
          otherFeeColumns={otherFeeColumns}
          dynamicColumns={dynamicColumns}
          manipulateDynamicData={manipulateDynamicData}
        ></ActivityDetailsModal>
      )}
      {modal}
    </form>
  );
}
